// animation-timeline.component.ts
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { AnimationFrame, emptyFrame } from '../../../models/led-board.model';
import { LedBoardService } from '../../../services/led-board.service';

@Component({
  selector: 'app-animation-timeline',
  templateUrl: './animation-timeline.component.html',
  styleUrls: ['./animation-timeline.component.scss'],
  standalone: false,
})
export class AnimationTimelineComponent implements OnChanges {
  @Input() frames: AnimationFrame[] = [{ ms: 120, pixels: emptyFrame() }];
  @Output() framesChange = new EventEmitter<AnimationFrame[]>();
  @Input() activeIndex = 0;
  @Output() activeIndexChange = new EventEmitter<number>();
  /** Asks the page to put the timeline away again (single-frame drawings only). */
  @Output() hide = new EventEmitter<void>();

  open = this.readOpen();
  private thumbCache = new Map<AnimationFrame, string>();

  constructor(private board: LedBoardService) {}

  private readOpen(): boolean {
    try { return localStorage.getItem('lbTimelineOpen') === '1'; } catch { return false; }
  }
  setOpen(v: boolean) {
    this.open = v;
    try { localStorage.setItem('lbTimelineOpen', v ? '1' : '0'); } catch {}
  }
  toggleOpen() { this.setOpen(!this.open); }

  ngOnChanges(changes: SimpleChanges) {
    const fc = changes['frames'];
    if (fc && !fc.firstChange) {
      const prev = (fc.previousValue as AnimationFrame[]) || [];
      const cur = (fc.currentValue as AnimationFrame[]) || [];
      if (prev.length <= 1 && cur.length > 1) { this.setOpen(true); }
    }
  }

  /** Hiding is only safe while there is nothing to lose. */
  get canHide(): boolean { return this.frames.length <= 1; }

  thumb(f: AnimationFrame): string {
    let url = this.thumbCache.get(f);
    if (url === undefined) { url = this.board.makeThumbnail(f.pixels); this.thumbCache.set(f, url); }
    return url;
  }

  stepMs(delta: number) {
    this.setMs(this.activeIndex, this.frames[this.activeIndex].ms + delta);
  }

  private commit() { this.framesChange.emit(this.frames); }
  select(i: number) { this.activeIndex = i; this.activeIndexChange.emit(i); }

  addFrame() {
    this.setOpen(true);
    const pixels = this.frames[this.activeIndex].pixels.slice();   // carry forward
    this.frames = [...this.frames, { ms: 120, pixels }];
    this.commit(); this.select(this.frames.length - 1);
  }
  duplicateFrame(i: number) {
    const copy = { ms: this.frames[i].ms, pixels: this.frames[i].pixels.slice() };
    this.frames = [...this.frames.slice(0, i + 1), copy, ...this.frames.slice(i + 1)];
    this.commit(); this.select(i + 1);
  }
  deleteFrame(i: number) {
    if (this.frames.length <= 1) { return; }
    this.frames = this.frames.filter((_, k) => k !== i);
    this.commit(); this.select(Math.max(0, Math.min(i, this.frames.length - 1)));
  }
  moveFrame(i: number, dir: number) {
    const j = i + dir;
    if (j < 0 || j >= this.frames.length) { return; }
    const next = this.frames.slice();
    [next[i], next[j]] = [next[j], next[i]];
    this.frames = next; this.commit(); this.select(j);
  }
  setMs(i: number, ms: number) {
    const next = this.frames.slice();
    next[i] = { ...next[i], ms: Math.max(20, ms) };
    this.frames = next; this.commit();
  }
}
